import { useCallback, useEffect, useMemo, useState } from 'react'
import { Activity, AlertCircle, Edit2, Loader2, Plus, RotateCcw, Settings, Trash2, Wand2 } from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'
import { Button } from '../components/ui/button'
import {
  activateHsiConfig,
  createHsiConfig,
  deleteHsiConfig,
  getAllHsiConfigs,
  getDefaultHsiConfig,
  testHsi,
  updateHsiConfig,
  type HikeStrongIndexConfigDto,
  type HsiBandDto
} from '../lib/api/hikeStrongIndex'

const ZERO_PLAN_OPTIONS = [
  { value: 0, label: 'Treat as Perfect' },
  { value: 1, label: 'Ignore in Score' },
  { value: 2, label: 'Use Epsilon' }
]

const DEFAULT_BANDS: HsiBandDto[] = [
  { id: 'poor', label: 'Poor', minPercent: 0, maxPercent: 50 },
  { id: 'fair', label: 'Fair', minPercent: 51, maxPercent: 75 },
  { id: 'good', label: 'Good', minPercent: 76, maxPercent: 99 },
  { id: 'great', label: 'Great', minPercent: 100, maxPercent: 150 }
]

export const HikeStrongIndexConfigPage = () => {
  const [configs, setConfigs] = useState<HikeStrongIndexConfigDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [editingConfig, setEditingConfig] = useState<HikeStrongIndexConfigDto | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [activatingId, setActivatingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [resetting, setResetting] = useState(false)

  // Test harness
  const [testPlanId, setTestPlanId] = useState('')
  const [testAsOf, setTestAsOf] = useState('')
  const [testResult, setTestResult] = useState<string | null>(null)
  const [testing, setTesting] = useState(false)
  const [testError, setTestError] = useState<string | null>(null)
  const [useEditedForTest, setUseEditedForTest] = useState(false)

  const sortedConfigs = useMemo(
    () =>
      [...configs].sort((a, b) => {
        if (a.isActive) return -1
        if (b.isActive) return 1
        return (b.updatedAt || b.createdAt || '').localeCompare(a.updatedAt || a.createdAt || '')
      }),
    [configs]
  )

  const loadConfigs = useCallback(async () => {
    try {
      const data = await getAllHsiConfigs()
      setConfigs(data)
      setError(null)
    } catch (err) {
      console.error('Failed to load HSI configs', err)
      setError('Failed to load HSI configs')
    }
  }, [])

  useEffect(() => {
    setLoading(true)
    loadConfigs().finally(() => setLoading(false))
  }, [loadConfigs])

  const handleCreateNew = async () => {
    try {
      const defaults = await getDefaultHsiConfig()
      setEditingConfig({
        ...defaults,
        id: undefined,
        name: 'New HSI Config',
        isActive: false,
        bands: defaults.bands?.length ? defaults.bands : DEFAULT_BANDS
      })
      setIsCreating(true)
      setSaveError(null)
    } catch (err) {
      console.error('Failed to load default HSI config', err)
      setError('Failed to load default HSI config')
    }
  }

  const handleEdit = (config: HikeStrongIndexConfigDto) => {
    setEditingConfig({ ...config })
    setIsCreating(false)
    setSaveError(null)
  }

  const handleCancelEdit = () => {
    setEditingConfig(null)
    setIsCreating(false)
    setSaveError(null)
  }

  const handleSave = async () => {
    if (!editingConfig) return
    setSaving(true)
    setSaveError(null)

    try {
      if (isCreating) {
        await createHsiConfig(editingConfig)
      } else if (editingConfig.id) {
        await updateHsiConfig(editingConfig.id, editingConfig)
      }
      await loadConfigs()
      setEditingConfig(null)
      setIsCreating(false)
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data?.error ?? 'Failed to save'
          : 'Failed to save'
      setSaveError(message)
    } finally {
      setSaving(false)
    }
  }

  const handleActivate = async (id: string) => {
    setActivatingId(id)
    try {
      await activateHsiConfig(id)
      await loadConfigs()
    } catch (err) {
      console.error('Failed to activate HSI config', err)
      setError('Failed to activate HSI config')
    } finally {
      setActivatingId(null)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this HSI config?')) return
    setDeletingId(id)
    try {
      await deleteHsiConfig(id)
      await loadConfigs()
      if (editingConfig?.id === id) {
        setEditingConfig(null)
        setIsCreating(false)
      }
    } catch (err) {
      console.error('Failed to delete HSI config', err)
      setError('Failed to delete HSI config')
    } finally {
      setDeletingId(null)
    }
  }

  const handleResetToDefaults = async () => {
    setResetting(true)
    setError(null)
    try {
      const defaults = await getDefaultHsiConfig()
      const created = await createHsiConfig({
        ...defaults,
        id: undefined,
        name: 'Default HSI Config (Reset)',
        isActive: false,
        bands: defaults.bands?.length ? defaults.bands : DEFAULT_BANDS
      })
      await activateHsiConfig(created.id!)
      await loadConfigs()
    } catch (err) {
      console.error('Failed to reset to defaults', err)
      setError('Failed to reset to defaults')
    } finally {
      setResetting(false)
    }
  }

  const handleTest = async () => {
    if (!testPlanId) {
      setTestError('Plan ID is required')
      return
    }
    setTesting(true)
    setTestError(null)
    setTestResult(null)

    try {
      const configOverride =
        useEditedForTest && editingConfig
          ? { ...editingConfig, id: undefined }
          : undefined

      const response = await testHsi({
        planId: testPlanId,
        asOfUtc: testAsOf ? new Date(testAsOf).toISOString() : undefined,
        configOverride
      })

      if (!response.success) {
        setTestError(response.errorMessage || 'Test failed')
      } else if (response.result) {
        setTestResult(
          `HSI ${response.result.scorePercent.toFixed(1)} (${response.result.band}) — Distance ${response.result.distanceIndex.toFixed(
            2
          )}, Elevation ${response.result.elevationIndex.toFixed(2)}, Pack ${response.result.packIndex.toFixed(2)}`
        )
      }
    } catch (err) {
      console.error('Failed to test HSI', err)
      setTestError('Failed to test HSI')
    } finally {
      setTesting(false)
    }
  }

  const updateBand = (index: number, key: keyof HsiBandDto, value: string) => {
    setEditingConfig(prev => {
      if (!prev) return prev
      const bands = [...(prev.bands || [])]
      const band = { ...(bands[index] ?? DEFAULT_BANDS[index] ?? { id: `band-${index}`, minPercent: 0, label: '' }) }
      ;(band as Record<string, string | number | null>)[key] = value === '' ? null : isNaN(Number(value)) ? value : Number(value)
      bands[index] = band
      return { ...prev, bands }
    })
  }

  const updateNested = (path: string, value: number | string) => {
    setEditingConfig(prev => {
      if (!prev) return prev
      const next = { ...prev }
      switch (path) {
        case 'weights.distanceWeight':
          next.weights = { ...next.weights, distanceWeight: Number(value) }
          break
        case 'weights.elevationWeight':
          next.weights = { ...next.weights, elevationWeight: Number(value) }
          break
        case 'weights.packWeight':
          next.weights = { ...next.weights, packWeight: Number(value) }
          break
        case 'clamping.indexMin':
          next.clamping = { ...next.clamping, indexMin: Number(value) }
          break
        case 'clamping.indexMax':
          next.clamping = { ...next.clamping, indexMax: Number(value) }
          break
        case 'clamping.hsiPercentMin':
          next.clamping = { ...next.clamping, hsiPercentMin: Number(value) }
          break
        case 'clamping.hsiPercentMax':
          next.clamping = { ...next.clamping, hsiPercentMax: Number(value) }
          break
        case 'clamping.epsilon':
          next.clamping = { ...next.clamping, epsilon: Number(value) }
          break
        case 'metricBehavior.distanceZeroPlanMode':
          next.metricBehavior = { ...next.metricBehavior, distanceZeroPlanMode: Number(value) as 0 | 1 | 2 }
          break
        case 'metricBehavior.elevationZeroPlanMode':
          next.metricBehavior = { ...next.metricBehavior, elevationZeroPlanMode: Number(value) as 0 | 1 | 2 }
          break
        case 'metricBehavior.packZeroPlanMode':
          next.metricBehavior = { ...next.metricBehavior, packZeroPlanMode: Number(value) as 0 | 1 | 2 }
          break
        default:
          break
      }
      return next
    })
  }

  return (
    <div className="space-y-4">
      <header className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-sm uppercase tracking-wide text-muted-foreground">HikeStrong Index</p>
          <h1 className="text-3xl font-semibold text-foreground">HSI Config</h1>
          <p className="text-sm text-muted-foreground">
            Manage HikeStrong Index weights, clamping, and band thresholds. Use the tester to try a plan.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleResetToDefaults} disabled={resetting || !!editingConfig}>
            {resetting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
            Reset to Defaults
          </Button>
          <Button onClick={handleCreateNew} disabled={!!editingConfig}>
            <Plus className="mr-2 h-4 w-4" />
            New Config
          </Button>
        </div>
      </header>

      {error && (
        <Card className="border-destructive">
          <CardContent className="flex items-center gap-2 py-4 text-destructive">
            <AlertCircle className="h-5 w-5" />
            <span>{error}</span>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="flex items-center gap-2 py-12 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>Loading HSI configs…</span>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[1.3fr,1fr]">
          <div className="space-y-3">
            {sortedConfigs.map(config => (
              <Card key={config.id} className={config.isActive ? 'border-primary' : ''}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <div>
                    <CardTitle className="text-lg">{config.name}</CardTitle>
                    <CardDescription>
                      v{config.version} • {config.isActive ? 'Active' : 'Inactive'}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(config)}>
                      <Edit2 className="mr-1 h-4 w-4" />
                      Edit
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleActivate(config.id!)}
                      disabled={activatingId === config.id}
                    >
                      {activatingId === config.id ? (
                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                      ) : (
                        <Settings className="mr-1 h-4 w-4" />
                      )}
                      {config.isActive ? 'Active' : 'Activate'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(config.id!)}
                      disabled={deletingId === config.id}
                    >
                      {deletingId === config.id ? (
                        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="mr-1 h-4 w-4" />
                      )}
                      Delete
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2 text-sm text-muted-foreground">
                  <div>Weights — D:{config.weights.distanceWeight}, E:{config.weights.elevationWeight}, P:{config.weights.packWeight}</div>
                  <div>
                    Clamping — Index [{config.clamping.indexMin}, {config.clamping.indexMax}], HSI% [{config.clamping.hsiPercentMin},{' '}
                    {config.clamping.hsiPercentMax}]
                  </div>
                  <div>
                    Bands — {config.bands.map(b => `${b.label} (${b.minPercent}-${b.maxPercent ?? '∞'})`).join(', ')}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="space-y-3">
            {/* Editor */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  {isCreating ? 'Create HSI Config' : editingConfig ? 'Edit HSI Config' : 'HSI Config Editor'}
                </CardTitle>
                <CardDescription>Adjust weights, clamping, and band thresholds.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {saveError && (
                  <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{saveError}</div>
                )}
                {editingConfig ? (
                  <div className="space-y-3">
                    <div className="grid gap-2 sm:grid-cols-2">
                      <Input
                        value={editingConfig.name}
                        onChange={e => setEditingConfig(c => (c ? { ...c, name: e.target.value } : c))}
                        placeholder="Name"
                      />
                      <Input
                        value={editingConfig.version}
                        onChange={e => setEditingConfig(c => (c ? { ...c, version: e.target.value } : c))}
                        placeholder="Version"
                      />
                    </div>
                    <Textarea
                      value={editingConfig.notes ?? ''}
                      onChange={e => setEditingConfig(c => (c ? { ...c, notes: e.target.value } : c))}
                      placeholder="Notes (optional)"
                    />

                    <div className="grid gap-2 sm:grid-cols-3">
                      <Input
                        type="number"
                        step="0.05"
                        value={editingConfig.weights.distanceWeight}
                        onChange={e => updateNested('weights.distanceWeight', e.target.value)}
                        placeholder="Distance weight"
                      />
                      <Input
                        type="number"
                        step="0.05"
                        value={editingConfig.weights.elevationWeight}
                        onChange={e => updateNested('weights.elevationWeight', e.target.value)}
                        placeholder="Elevation weight"
                      />
                      <Input
                        type="number"
                        step="0.05"
                        value={editingConfig.weights.packWeight}
                        onChange={e => updateNested('weights.packWeight', e.target.value)}
                        placeholder="Pack weight"
                      />
                    </div>

                    <div className="grid gap-2 sm:grid-cols-3">
                      <Input
                        type="number"
                        value={editingConfig.clamping.indexMin}
                        onChange={e => updateNested('clamping.indexMin', e.target.value)}
                        placeholder="Index min"
                      />
                      <Input
                        type="number"
                        value={editingConfig.clamping.indexMax}
                        onChange={e => updateNested('clamping.indexMax', e.target.value)}
                        placeholder="Index max"
                      />
                      <Input
                        type="number"
                        value={editingConfig.clamping.epsilon}
                        onChange={e => updateNested('clamping.epsilon', e.target.value)}
                        placeholder="Epsilon"
                      />
                    </div>

                    <div className="grid gap-2 sm:grid-cols-2">
                      <Input
                        type="number"
                        value={editingConfig.clamping.hsiPercentMin}
                        onChange={e => updateNested('clamping.hsiPercentMin', e.target.value)}
                        placeholder="HSI% min"
                      />
                      <Input
                        type="number"
                        value={editingConfig.clamping.hsiPercentMax}
                        onChange={e => updateNested('clamping.hsiPercentMax', e.target.value)}
                        placeholder="HSI% max"
                      />
                    </div>

                    <div className="grid gap-2 sm:grid-cols-3">
                      {['distanceZeroPlanMode', 'elevationZeroPlanMode', 'packZeroPlanMode'].map(key => (
                        <select
                          key={key}
                          className="rounded-md border bg-background p-2 text-sm"
                          value={(editingConfig.metricBehavior as Record<string, number>)[key] ?? 0}
                          onChange={e => updateNested(`metricBehavior.${key}`, e.target.value)}
                        >
                          <option disabled>{key}</option>
                          {ZERO_PLAN_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      ))}
                    </div>

                    <div className="space-y-2">
                      <div className="text-sm font-medium text-foreground">Bands</div>
                      {(editingConfig.bands?.length ? editingConfig.bands : DEFAULT_BANDS).map((band, idx) => (
                        <div key={band.id || idx} className="grid gap-2 sm:grid-cols-4">
                          <Input
                            value={band.label}
                            onChange={e => updateBand(idx, 'label', e.target.value)}
                            placeholder="Label"
                          />
                          <Input
                            type="number"
                            value={band.minPercent}
                            onChange={e => updateBand(idx, 'minPercent', e.target.value)}
                            placeholder="Min %"
                          />
                          <Input
                            type="number"
                            value={band.maxPercent ?? ''}
                            onChange={e => updateBand(idx, 'maxPercent', e.target.value)}
                            placeholder="Max %"
                          />
                          <Input
                            value={band.id}
                            onChange={e => updateBand(idx, 'id', e.target.value)}
                            placeholder="ID"
                          />
                        </div>
                      ))}
                    </div>

                    <div className="flex gap-2">
                      <Button onClick={handleSave} disabled={saving}>
                        {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                        {isCreating ? 'Create' : 'Save'}
                      </Button>
                      <Button variant="ghost" onClick={handleCancelEdit}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Select a config to edit or create a new one.</p>
                )}
              </CardContent>
            </Card>

            {/* Tester */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wand2 className="h-4 w-4" />
                  HSI Tester
                </CardTitle>
                <CardDescription>Run the HSI calculation for a plan ID.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {testError && (
                  <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{testError}</div>
                )}
                {testResult && (
                  <div className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700 dark:bg-emerald-950 dark:text-emerald-200">
                    {testResult}
                  </div>
                )}
                <Input
                  value={testPlanId}
                  onChange={e => setTestPlanId(e.target.value)}
                  placeholder="Plan ID"
                />
                <Input
                  type="datetime-local"
                  value={testAsOf}
                  onChange={e => setTestAsOf(e.target.value)}
                  placeholder="As of (UTC)"
                />
                <label className="flex items-center gap-2 text-sm text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={useEditedForTest}
                    onChange={e => setUseEditedForTest(e.target.checked)}
                    disabled={!editingConfig}
                  />
                  Use edited config as override (optional)
                </label>
                <Button onClick={handleTest} disabled={testing}>
                  {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Run Test
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  )
}

